/**
 * Turn a stored key into something readable.
 *
 * Two shapes, because the product genuinely wants both and conflating them
 * would change copy on live screens:
 *
 * - :func:`titleCaseKey` - ``not_covered`` -> ``Not Covered``. For a value
 *   standing on its own: a select option with no authored label, a badge, a
 *   column cell.
 * - :func:`sentenceCaseKey` - ``holding_id`` -> ``Holding ID``. For a field
 *   NAME read as prose, as in an audit log line or an error message.
 *
 * This existed seven times over before it lived here - three identical, one a
 * superset, one inlined, and two under this very name with the other casing.
 */

/** Separators to spaces, runs collapsed, trimmed. Shared by both. */
function words(value: unknown): string {
  return String(value ?? '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * ``not_covered`` -> ``Not Covered``. Every word capitalised.
 *
 * Splits on the spaces ``words`` already normalised rather than using ``\b``,
 * which is ASCII-only in JS: ``/\b\p{L}/u`` finds a boundary INSIDE "étape"
 * and yields "éTape". Every copy of this that reached for ``\b`` has that bug.
 */
export function titleCaseKey(value: unknown): string {
  return words(value)
    .split(' ')
    .map((word) => (word ? word[0].toUpperCase() + word.slice(1) : word))
    .join(' ')
}

/**
 * ``holding_id`` -> ``Holding ID``. First word capitalised, the rest left as
 * prose, and ``id`` read as the acronym it is.
 */
export function sentenceCaseKey(value: unknown): string {
  return words(value)
    .replace(/\bid\b/gi, 'ID')
    .replace(/^./, (c) => c.toUpperCase())
}
